import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button, Card, EmptyState, Screen, Text } from "@andrum/ui";
import {
  advanceProgramDay,
  enrollInProgram,
  getActiveEnrollment,
  listPrograms,
  startProgramDayExperiment,
  type Enrollment,
  type ProgramSummary,
} from "@/features/programs/api/programs-api";
import {
  ProgramDayDetail,
  ProgramDayGrid,
} from "@/features/programs/components/ProgramDayGrid";
import {
  programDayMeta,
  progressPercent,
  slugForProgramDay,
} from "@/features/programs/data/program-days";
import { useExperimentStore } from "@/stores/experiment-store";
import { hapticService } from "@/lib/capacitor";
import { canUsePremiumPrograms } from "@/lib/billing/premium-gate";
import { listPublishedExperiments } from "@/features/experiments/api/experiments-api";
import { PremiumGateCard } from "@/components/premium/PremiumGateCard";

/** Soft beta: core 21-day program is free. Future premium program IDs gate here. */
const FREE_PROGRAM_IDS = new Set([
  "33333333-3333-4333-8333-333333333301",
]);

export function ProgramsScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState<ProgramSummary[]>([]);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(1);
  const [hasProgramPlus, setHasProgramPlus] = useState(false);
  const [dayTitle, setDayTitle] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, active, entitled] = await Promise.all([
        listPrograms(),
        getActiveEnrollment(),
        canUsePremiumPrograms(),
      ]);
      setHasProgramPlus(entitled);
      setPrograms(list);
      setEnrollment(active);
      if (active) setSelectedDay(active.currentDay);
    } catch {
      setError(t("programs.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selectedMeta = useMemo(
    () => programDayMeta(selectedDay),
    [selectedDay]
  );

  useEffect(() => {
    void (async () => {
      const catalog = await listPublishedExperiments();
      const slug = slugForProgramDay(selectedDay);
      const match = catalog.find((e) => e.slug === slug);
      setDayTitle(match?.title ?? null);
    })();
  }, [selectedDay]);

  const enroll = async (programId: string) => {
    const isFree = FREE_PROGRAM_IDS.has(programId);
    if (!isFree && !hasProgramPlus) {
      void navigate({
        to: "/app/paywall",
        search: { reason: "programs" },
      });
      return;
    }
    setBusy(true);
    setMessage(null);
    const result = await enrollInProgram(programId);
    setBusy(false);
    if (!result.ok) {
      setMessage(result.message);
      return;
    }
    void hapticService.success();
    setEnrollment(result.enrollment);
    setSelectedDay(result.enrollment.currentDay);
    setMessage(t("programs.started"));
  };

  const startDay = async (day: number) => {
    setBusy(true);
    setMessage(null);
    const { assignment, experiment } = await startProgramDayExperiment(
      day !== enrollment?.currentDay ? day : undefined
    );
    if (assignment && experiment) {
      useExperimentStore.setState((state) => ({
        experiments: state.experiments.some((e) => e.id === experiment.id)
          ? state.experiments
          : [...state.experiments, experiment],
        assignments: [
          assignment,
          ...state.assignments.filter(
            (a) =>
              a.id !== assignment.id &&
              a.status !== "assigned" &&
              a.status !== "started"
          ),
        ],
      }));
      void hapticService.selection();
      setBusy(false);
      void navigate({
        to: "/experiment/$assignmentId",
        params: { assignmentId: assignment.id },
      });
      return;
    }
    setBusy(false);
    setMessage(t("programs.notFoundDay"));
  };

  const markDayDone = async () => {
    setBusy(true);
    setMessage(null);
    const result = await advanceProgramDay();
    setBusy(false);
    if (!result.ok) {
      setMessage(result.message);
      return;
    }
    void hapticService.success();
    setEnrollment(result.enrollment);
    if (result.enrollment) {
      setSelectedDay(result.enrollment.currentDay);
      setMessage(t("programs.advanced", { day: result.enrollment.currentDay }));
    } else {
      setMessage(t("programs.finished"));
    }
  };

  if (loading) {
    return <Screen loading skeleton loadingLabel={t("programs.loading")} />;
  }

  if (error) {
    return (
      <Screen>
        <EmptyState
          title={t("programs.loadErrorTitle")}
          description={error}
          action={
            <Button fullWidth onClick={() => void refresh()}>
              {t("retry")}
            </Button>
          }
        />
      </Screen>
    );
  }

  return (
    <Screen>
      <header className="shrink-0 space-y-1">
        <Text variant="title" as="h1">
          {t("programs.title")}
        </Text>
        <Text variant="muted" className="line-clamp-2 text-sm leading-snug">
          {t("programs.lead")}
        </Text>
      </header>

      {enrollment ? (
        <Card className="space-y-3 p-4">
          <div>
            <Text variant="caption">{t("programs.active")}</Text>
            <Text variant="heading" as="h2">
              {enrollment.programTitle}
            </Text>
            <Text variant="body" className="mt-1 text-sm">
              {t("programs.dayOf", {
                day: enrollment.currentDay,
                total: enrollment.durationDays,
              })}
            </Text>
          </div>

          <div className="space-y-1">
            <div className="flex justify-between text-xs text-foreground-subtle">
              <span>{t("programs.progress")}</span>
              <span className="tabular-nums">
                {progressPercent(
                  enrollment.currentDay,
                  enrollment.durationDays
                )}
                %
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-surface-muted">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{
                  width: `${progressPercent(
                    enrollment.currentDay,
                    enrollment.durationDays
                  )}%`,
                }}
              />
            </div>
          </div>

          <ProgramDayGrid
            currentDay={enrollment.currentDay}
            durationDays={enrollment.durationDays}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
          />

          <ProgramDayDetail day={selectedMeta} />
          {dayTitle ? (
            <Text variant="subtle">
              {t("programs.experiment")}{" "}
              <strong className="text-foreground">{dayTitle}</strong>
            </Text>
          ) : null}

          <Button
            fullWidth
            loading={busy}
            onClick={() => void startDay(selectedDay)}
          >
            {selectedDay === enrollment.currentDay
              ? t("programs.doToday")
              : selectedDay < enrollment.currentDay
                ? t("programs.redo")
                : t("programs.previewLocked")}
          </Button>
          {selectedDay > enrollment.currentDay ? (
            <Text variant="caption">{t("programs.futureHint")}</Text>
          ) : null}

          {selectedDay === enrollment.currentDay ? (
            <Button
              fullWidth
              variant="soft"
              loading={busy}
              onClick={() => void markDayDone()}
            >
              {t("programs.markDone")}
            </Button>
          ) : null}
        </Card>
      ) : (
        <EmptyState
          title={t("programs.emptyTitle")}
          description={t("programs.emptyBody")}
        />
      )}

      {programs.map((p) => {
        const isFree = FREE_PROGRAM_IDS.has(p.id);
        const locked = !isFree && !hasProgramPlus;
        return (
          <Card key={p.id} className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Text variant="heading" as="h2">
                {p.title}
              </Text>
              {!isFree ? (
                <span className="rounded-full bg-primary-soft px-2.5 py-0.5 text-xs font-medium text-primary">
                  {t("plus")}
                </span>
              ) : (
                <span className="rounded-full bg-secondary-soft px-2.5 py-0.5 text-xs font-medium text-secondary">
                  {t("free")}
                </span>
              )}
            </div>
            <Text variant="subtle">{p.summary}</Text>
            <Text variant="caption">
              {t("programs.daysNoStreak", { days: p.durationDays })}
            </Text>
            {enrollment?.programId === p.id ? (
              <Text variant="caption">{t("programs.isActive")}</Text>
            ) : locked ? (
              <Button
                fullWidth
                variant="soft"
                onClick={() =>
                  void navigate({
                    to: "/app/paywall",
                    search: { reason: "programs" },
                  })
                }
              >
                {t("programs.unlockPlus")}
              </Button>
            ) : (
              <Button
                fullWidth
                variant={enrollment ? "outline" : "primary"}
                loading={busy}
                onClick={() => void enroll(p.id)}
              >
                {enrollment ? t("programs.switchTo") : t("programs.start")}
              </Button>
            )}
          </Card>
        );
      })}

      {!hasProgramPlus ? (
        <PremiumGateCard
          compact
          reason="programs"
          title={t("programs.plusCardTitle")}
          description={t("programs.plusCardBody")}
        />
      ) : null}

      {message ? (
        <Text variant="caption" className="text-center">
          {message}
        </Text>
      ) : null}
    </Screen>
  );
}
